import useSWR from 'swr';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
  CardJobDetailInListHead,
  CardJobDetailInListBox,
} from '@/src/component/superstack/jobs/cards/CardJobDetailInList';
import { Checkbox } from '@/src/component/superstack/base/Checkbox';
import { InputSearchGroup } from '@/src/component/superstack/base/InputSearchGroup';
import { Input } from '@/src/component/superstack/base/Inputs';
import { ChipFilterToggle } from '@/src/component/superstack/base/ChipFilterToggle';
import { useStaticData } from '@/src/utils/hooks/superstack/useStaticData';
import { useSidos, useSigungus } from '@/src/utils/hooks/superstack/useAddress';
import { api, internalApi } from '@/src/utils/api';

import { MSSLayout, SSLayout } from '@/src/layout/superstack/SSLayout';
import { SSModal, SSModal2 } from '@/src/component/superstack/base/Modal';
import { MSSModal } from '@/src/component/superstack/base/MModal';
import { SSPagination } from '@/src/component/superstack/base/Pagination';
import { StackModal, MStackModal } from '@/src/component/superstack/modals/StackModal';
import {
  FilteredStacks,
  FilteredRoles,
  FilteredLevels,
  FilteredSigungus,
  FilterKeyword,
  FilterPage,
  JobSort,
} from '@/src/store/superstack/jobs/FilterStates';
import { ChipFilterContainer } from '@/src/component/superstack/jobs/ChipFilterContainer';
import {
  FilterSaveModal,
  MFilterSaveModal,
} from '@/src/component/superstack/jobs/modals/FilterSaveModal';
import {
  FilterApplyModal,
  MFilterApplyModal,
} from '@/src/component/superstack/jobs/modals/FilterApplyModal';
import FadeLoader from 'react-spinners/FadeLoader';

import IconCheck from '@/asset/superstack/svgs/icon-check.svg';
import IconArrowCircleLeft from '@/asset/superstack/svgs/icon-arrow-circle-left.svg';
import IconArrowRight from '@/asset/superstack/svgs/icon-arrow-right.svg';
import IconSave from '@/asset/superstack/svgs/icon-save.svg';
import IconFilter from '@/asset/superstack/svgs/icon-filter.svg';
import IconX from '@/asset/superstack/svgs/x.svg';
import ArrowRotateLeft from '@/asset/superstack/svgs/arrow-rotate-left.svg';

import { GeneralLinks } from '@/data/superstack/GeneralLinks';
import { ApiUrls } from '@/data/superstack/ApiUrls';
import { JobLinks } from '@/data/superstack/JobLinks';

import classNames from 'classnames/bind';
import styles from '@/styles/superstack/jobs/index.module.scss';
import m_styles from '@/styles/superstack/jobs/m.index.module.scss';
import { getJobDisplayingUrlObj } from '@/src/utils/superstack/jobs';
import { CardJobList, CardJobDividedList } from '@/src/component/superstack/jobs/cards/CardJobList';
import { JobSortWrapper } from '@/src/component/superstack/jobs/JobSortWrapper';
import { useDebouncedEffect } from '@/src/utils/hooks/useDebouncedEffect';
import { MSelectedSigunguChips } from '@/src/component/superstack/base/Box';

const cx = classNames.bind(styles);
const mcx = classNames.bind(m_styles);

const PAGINATED_BY = 20;

const JobDisplayingListWrapper = ({ userInfoData, ...props }) => {
  // For static data, rerender only when query on changed.

  const isMobile = props.isMobile;
  const router = useRouter();
  const {
    filter_data = '',
    keyword,
    sort,
    page,
  } = {
    ...router.query,
  };
  const filterData = JSON.parse(filter_data.replace(/\?filter_data=.*/, '') || '{}');

  const { roles = [], levels = [], stacks = [], isLoading: isStaticDataLoading } = useStaticData();
  const { addresses: { data: sidos = [] } = {}, isLoading: isSidosLoading } = useSidos();
  const { addresses: { data: sigungus = [] } = {}, isLoading: isSigungusLoading } = useSigungus();
  const staticData = {
    roles: roles,
    levels: levels,
    stacks: stacks,
    sidos: sidos,
    sigungus: sigungus,
  };
  return (
    <>
      {!isStaticDataLoading && !isSidosLoading && !isSigungusLoading && (
        <JobDisplayingListContainer
          filterData={filterData}
          keyword={keyword}
          sort={sort}
          page={page}
          staticData={staticData}
          isMobile={isMobile}
          userInfoData={userInfoData}
          router={router}
        />
      )}
    </>
  );
};

const JobDisplayingListContainer = ({
  filterData,
  keyword,
  sort,
  page,
  staticData,
  isMobile,
  userInfoData,
  router,
}) => {
  const [filteredStacks, setFilteredStacks] = useRecoilState(FilteredStacks);
  const [filteredRoles, setFilteredRoles] = useRecoilState(FilteredRoles);
  const [filteredLevels, setFilteredLevels] = useRecoilState(FilteredLevels);
  const [filteredSigungus, setFilteredSigungus] = useRecoilState(FilteredSigungus);
  const [keyword_, setKeyword_] = useRecoilState(FilterKeyword);
  const [sort_, setSort_] = useRecoilState(JobSort);
  const [page_, setPage_] = useRecoilState(FilterPage);

  // 페이지 params와 필터 데이터 동기화
  useEffect(() => {
    setFilteredStacks(filterData.filteredStacks || {});
    setFilteredRoles(filterData.filteredRoles || []);
    setFilteredLevels(filterData.filteredLevels || []);
    setFilteredSigungus(filterData.filteredSigungus || []);
    setKeyword_(keyword || '');
    setSort_(sort || 'default');
    setPage_(page * 1 || 1);
  }, []);

  // 필터 데이터와 url 동기화
  useEffect(() => {
    const url = getJobDisplayingUrlObj(
      filteredRoles,
      filteredLevels,
      filteredStacks,
      filteredSigungus,
      keyword_,
      sort_,
      page_,
      router.query,
    );
    router.replace(url, url, { shallow: true, scroll: false });
  }, [filteredRoles, filteredLevels, filteredStacks, filteredSigungus, keyword_, sort_, page_]);

  const [savedFilters, setSavedFilters] = useState([]);
  const [isModalStackOpen, setIsModalStackOpen] = useState(false);
  const [isModalRoleOpen, setIsModalRoleOpen] = useState(false);
  const [isModalLevelOpen, setIsModalLevelOpen] = useState(false);
  const [isModalAddressOpen, setIsModalAddressOpen] = useState(false);
  const [isFilterSaveModalOpen, setIsFilterSaveModalOpen] = useState(false);
  const setIsFilterSaveModalOpen_ = (isOpen) => {
    if (!userInfoData?.username) {
      alert('필터를 저장하려면 로그인이 필요합니다.');
      return false;
    }
    setIsFilterSaveModalOpen(isOpen);
  };
  const [isFilterApplyModalOpen, setIsFilterApplyModalOpen] = useState(false);
  const [isMFilterSectionOpen, setIsMFilterSectionOpen] = useState(false);

  const handlePageChange = (e, page) => {
    if (!page) return;
    setPage_(page);
    window.scrollTo(0, 0);
  };

  // fetch jobs
  const url = GeneralLinks().find((link) => link.name === 'displaying_list')?.url;
  const params = {
    filter_data: {
      filteredStacks: filteredStacks,
      filteredRoles: filteredRoles,
      filteredLevels: filteredLevels,
      filteredSigungus: filteredSigungus,
    },
    keyword: keyword_,
    sort: sort_,
    page: page_,
  };
  const jobsFetcher = (url, params) => {
    return api
      .get(url, {
        params: params,
      })
      .then((result) => result.data);
  };
  const { data: { jobs, total_jobs_cnt } = {} } = useSWR([url, params], (url) =>
    jobsFetcher(url, params),
  );

  // fetch filters
  const filterUrl = ApiUrls().find((url) => url.name === 'jobs_filters').url;
  const filtersFetcher = (url) => {
    return api.get(url, {}).then((result) => result.data);
  };
  const { data: filters, mutate } = useSWR([filterUrl, savedFilters], (url) => filtersFetcher(url));

  const isLoading = () => {
    return !total_jobs_cnt && total_jobs_cnt !== 0;
  };

  return isMobile ? (
    <MSSLayout pageTitle={'포지션 찾기'} sxFooter={{ marginTop: '0px' }}>
      <MJobDisplayingListPresenter
        savedFilters={savedFilters}
        setSavedFilters={setSavedFilters}
        isMFilterSectionOpen={isMFilterSectionOpen}
        isModalStackOpen={isModalStackOpen}
        isModalRoleOpen={isModalRoleOpen}
        isModalLevelOpen={isModalLevelOpen}
        isModalAddressOpen={isModalAddressOpen}
        setIsMFilterSectionOpen={setIsMFilterSectionOpen}
        setIsModalStackOpen={setIsModalStackOpen}
        setIsModalRoleOpen={setIsModalRoleOpen}
        setIsModalLevelOpen={setIsModalLevelOpen}
        setIsModalAddressOpen={setIsModalAddressOpen}
        isFilterSaveModalOpen={isFilterSaveModalOpen}
        isFilterApplyModalOpen={isFilterApplyModalOpen}
        setIsFilterSaveModalOpen={setIsFilterSaveModalOpen_}
        setIsFilterApplyModalOpen={setIsFilterApplyModalOpen}
        filters={filters}
        mutate={mutate}
        staticData={staticData}
        jobs={jobs}
        total_jobs_cnt={total_jobs_cnt}
        page={page_}
        handlePageChange={handlePageChange}
        keyword={keyword_}
        setKeyword={setKeyword_}
        isLoading={isLoading()}
      />
    </MSSLayout>
  ) : (
    <SSLayout
      pageTitle={'포지션 찾기 | 위시켓 잡스'}
      width={'100%'}
      sx={{ position: 'relative', margin: '0 auto 100px' }}
    >
      <JobDisplayingListPresenter
        savedFilters={savedFilters}
        setSavedFilters={setSavedFilters}
        isModalStackOpen={isModalStackOpen}
        isModalRoleOpen={isModalRoleOpen}
        isModalLevelOpen={isModalLevelOpen}
        isModalAddressOpen={isModalAddressOpen}
        setIsModalStackOpen={setIsModalStackOpen}
        setIsModalRoleOpen={setIsModalRoleOpen}
        setIsModalLevelOpen={setIsModalLevelOpen}
        setIsModalAddressOpen={setIsModalAddressOpen}
        isFilterSaveModalOpen={isFilterSaveModalOpen}
        isFilterApplyModalOpen={isFilterApplyModalOpen}
        setIsFilterSaveModalOpen={setIsFilterSaveModalOpen_}
        setIsFilterApplyModalOpen={setIsFilterApplyModalOpen}
        filters={filters}
        mutate={mutate}
        staticData={staticData}
        jobs={jobs}
        total_jobs_cnt={total_jobs_cnt}
        page={page_}
        handlePageChange={handlePageChange}
        keyword={keyword_}
        setKeyword={setKeyword_}
        userInfoData={userInfoData}
        isLoading={isLoading()}
      />
    </SSLayout>
  );
};

const JobDisplayingListPresenter = ({
  setSavedFilters,
  isModalStackOpen,
  isModalRoleOpen,
  isModalLevelOpen,
  isModalAddressOpen,
  setIsModalStackOpen,
  setIsModalRoleOpen,
  setIsModalLevelOpen,
  setIsModalAddressOpen,
  isFilterSaveModalOpen,
  isFilterApplyModalOpen,
  setIsFilterSaveModalOpen,
  setIsFilterApplyModalOpen,
  filters,
  mutate,
  staticData,
  jobs,
  total_jobs_cnt,
  page,
  handlePageChange,
  keyword,
  setKeyword,
  userInfoData,
  isLoading,
}) => {
  const filteredStacks = useRecoilValue(FilteredStacks);
  const filteredRoles = useRecoilValue(FilteredRoles);
  const filteredLevels = useRecoilValue(FilteredLevels);
  const filteredSigungus = useRecoilValue(FilteredSigungus);
  const [tempKeyword_, setTempKeyword_] = useState('');
  const [sort_, setSort_] = useRecoilState(JobSort);
  const [selectedJob, setSelectedJob] = useState({});

  useEffect(() => {
    if (page < 1) {
      handlePageChange(null, 1);
    }
    if (total_jobs_cnt >= 0) {
      const maxPage =
        total_jobs_cnt === 0 ? 1 : Math.floor((total_jobs_cnt - 1) / PAGINATED_BY) + 1;
      if (page > maxPage) {
        handlePageChange(null, maxPage);
      }
    }
  }, [page, total_jobs_cnt]);

  useEffect(() => {
    setSelectedJob((jobs && jobs[0]) || {});
  }, [jobs]);

  useEffect(() => {
    setTempKeyword_(keyword);
  }, [keyword]);

  useDebouncedEffect(
    () => {
      setKeyword(tempKeyword_);
    },
    1000,
    [tempKeyword_],
  );

  const hasJobSelected = () => {
    return Object.keys(selectedJob).length > 0;
  };

  const onFilter = () => {
    return (
      filteredRoles.length > 0 ||
      filteredLevels.length > 0 ||
      filteredSigungus.length > 0 ||
      Object.keys(filteredStacks).length > 0
    );
  };

  return (
    <>
      <section className={cx('layout')}>
        <section className={cx('list')}>
          <section className={cx('list-header')} id="list_header">
            <>
              <div className={cx('filter-search')}>
                <div className={cx('chip-filter-container')}>
                  {filters && filters.length > 0 && (
                    <span className={cx('chip-filter-toggle-wrapper')}>
                      <ChipFilterToggle
                        handleModalOpen={() => {
                          setIsFilterApplyModalOpen(true);
                        }}
                        isModalOpen={isFilterApplyModalOpen}
                      >
                        저장한 필터
                      </ChipFilterToggle>
                      <span className={cx('chip-sep')} />
                    </span>
                  )}
                  <ChipFilterToggle
                    num={Object.keys(filteredStacks).length}
                    handleModalOpen={() => {
                      setIsModalStackOpen(true);
                    }}
                    isModalOpen={isModalStackOpen}
                  >
                    기술 스택
                  </ChipFilterToggle>
                  <ChipFilterToggle
                    num={filteredRoles.length}
                    handleModalOpen={() => {
                      setIsModalRoleOpen(true);
                    }}
                    isModalOpen={isModalRoleOpen}
                  >
                    역할
                  </ChipFilterToggle>
                  <ChipFilterToggle
                    num={filteredLevels.length}
                    handleModalOpen={() => {
                      setIsModalLevelOpen(true);
                    }}
                    isModalOpen={isModalLevelOpen}
                  >
                    레벨
                  </ChipFilterToggle>
                  <ChipFilterToggle
                    num={filteredSigungus.length}
                    handleModalOpen={() => {
                      setIsModalAddressOpen(true);
                    }}
                    isModalOpen={isModalAddressOpen}
                  >
                    지역
                  </ChipFilterToggle>
                </div>
                <div className={cx('search-box')}>
                  <InputSearchGroup size={'sm'}>
                    <Input
                      size={'sm'}
                      value={tempKeyword_}
                      placeholder={'기업, 공고를 검색해보세요.'}
                      onChange={(e) => {
                        setTempKeyword_(e.target.value);
                      }}
                    />
                  </InputSearchGroup>
                </div>
              </div>
              {filters && (
                <FilterApplyModal
                  isFilterApplyModalOpen={isFilterApplyModalOpen}
                  setIsFilterApplyModalOpen={setIsFilterApplyModalOpen}
                  filters={filters}
                  setSavedFilters={setSavedFilters}
                />
              )}
              <StackModal
                isModalStackOpen={isModalStackOpen}
                setIsModalStackOpen={setIsModalStackOpen}
                stacks={staticData.stacks}
              />
              <RoleModal
                isModalRoleOpen={isModalRoleOpen}
                setIsModalRoleOpen={setIsModalRoleOpen}
                roles={staticData.roles}
              />
              <LevelModal
                isModalLevelOpen={isModalLevelOpen}
                setIsModalLevelOpen={setIsModalLevelOpen}
                levels={staticData.levels}
              />
              <SidoSigunguModal
                isModalAddressOpen={isModalAddressOpen}
                setIsModalAddressOpen={setIsModalAddressOpen}
                staticData={staticData}
              />
            </>

            {onFilter() && (
              <FilterSection
                isFilterSaveModalOpen={isFilterSaveModalOpen}
                setIsFilterSaveModalOpen={setIsFilterSaveModalOpen}
                updateContainer={mutate}
              />
            )}
          </section>
          <section className={cx('list-body')}>
            <FadeLoader
              color={'gray'}
              loading={isLoading}
              cssOverride={{
                top: '300px',
                left: '540px',
              }}
            />
            <section className={cx('left')}>
              {!isLoading && total_jobs_cnt > 0 && (
                <div className={cx(['metadata'])}>
                  <p>{total_jobs_cnt}개의 포지션</p>
                  <JobSortWrapper sort={sort_} setSort={setSort_} />
                </div>
              )}
              {!isLoading && (
                <div className={cx('list-scrollable')}>
                  <CardJobList
                    jobs={jobs}
                    handleClick={(job) => {
                      setSelectedJob(job);
                    }}
                    selectedJob={selectedJob}
                    showEmpty={total_jobs_cnt === 0}
                  />
                </div>
              )}
              {total_jobs_cnt > 0 && (
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <SSPagination
                    countItems={total_jobs_cnt}
                    paginatiedBy={PAGINATED_BY}
                    page={page}
                    handlePageChange={handlePageChange}
                  />
                </div>
              )}
            </section>
            <section className={cx('right')}>
              {hasJobSelected() && (
                <CardJobDetailInListHead selectedJob={selectedJob}>
                  <CardJobDetailInListBox jobId={selectedJob.id} userInfoData={userInfoData} />
                </CardJobDetailInListHead>
              )}
            </section>
          </section>
        </section>
      </section>
    </>
  );
};

const FilterSection = ({ isFilterSaveModalOpen, setIsFilterSaveModalOpen, updateContainer }) => {
  const setFilteredStacks = useSetRecoilState(FilteredStacks);
  const setFilteredRoles = useSetRecoilState(FilteredRoles);
  const setFilteredLevels = useSetRecoilState(FilteredLevels);
  const setFilteredSigungus = useSetRecoilState(FilteredSigungus);
  const resetFilter = () => {
    setFilteredRoles([]);
    setFilteredLevels([]);
    setFilteredStacks({});
    setFilteredSigungus([]);
  };
  return (
    <>
      <div className={cx('chip-filters')}>
        <span style={{ marginRight: '8px', minWidth: '68px' }}>적용된 필터</span>
        <ChipFilterContainer type={'all'} cx={cx} />
        <span
          className={cx('btn-save')}
          onClick={() => {
            setIsFilterSaveModalOpen(true);
          }}
        >
          <IconSave width={11} height={12} fill={'#006DFF'} />
          필터 저장
        </span>
        <span
          className={cx('btn-reset')}
          onClick={() => {
            resetFilter();
          }}
        >
          <ArrowRotateLeft width={13} height={13} />
          <span className={cx(['init-stack-text'])}>초기화</span>
        </span>
      </div>
      <FilterSaveModal
        isFilterSaveModalOpen={isFilterSaveModalOpen}
        setIsFilterSaveModalOpen={setIsFilterSaveModalOpen}
        updateContainer={() => {
          updateContainer();
        }}
      />
    </>
  );
};

const RoleModal = ({ isModalRoleOpen, setIsModalRoleOpen, roles }) => {
  const [filteredRoles, setFilteredRoles] = useRecoilState(FilteredRoles);
  return (
    <SSModal
      isOpen={isModalRoleOpen}
      onClose={() => {
        setIsModalRoleOpen(false);
      }}
      onReset={() => {
        setFilteredRoles([]);
      }}
      customStyle={{ content: { width: '280px' } }}
      title={'역할'}
      hasCloseBtn={false}
      hasResetBtn={true}
      btnResetName={'초기화'}
    >
      <div className={cx(['scrollable-y'])} style={{ height: '50vh' }}>
        {roles && (
          <CheckboxWrapper
            array={roles}
            name="roles"
            size={'lg'}
            handleFilter={setFilteredRoles}
            checkedValues={filteredRoles}
          />
        )}
      </div>
    </SSModal>
  );
};

const LevelModal = ({ isModalLevelOpen, setIsModalLevelOpen, levels }) => {
  const [filteredLevels, setFilteredLevels] = useRecoilState(FilteredLevels);
  return (
    <SSModal
      isOpen={isModalLevelOpen}
      onClose={() => {
        setIsModalLevelOpen(false);
      }}
      onReset={() => {
        setFilteredLevels([]);
      }}
      customStyle={{ content: { width: '360px' } }}
      title={'레벨'}
      hasCloseBtn={false}
      hasResetBtn={true}
      btnResetName={'초기화'}
    >
      <div>
        {levels && (
          <CheckboxWrapper
            array={levels}
            name="levels"
            size={'lg'}
            handleFilter={setFilteredLevels}
            checkedValues={filteredLevels}
          />
        )}
      </div>
    </SSModal>
  );
};

const SidoSigunguModal = ({ isModalAddressOpen, setIsModalAddressOpen, staticData }) => {
  const [filteredSigungus, setFilteredSigungus] = useRecoilState(FilteredSigungus);
  const [selectedSido, setSelectedSido] = useState();
  const selectedSigungus =
    staticData.sigungus.length > 0 &&
    staticData.sigungus.filter(([, , , sido_name_kor]) => {
      return sido_name_kor === selectedSido;
    });

  const [checkIsAllSelectedSido, setCheckIsAllSelectedSido] = useState([]);
  const deleteSido = (sidoSigungu) => {
    const sigunguSplit = sidoSigungu.split(';');
    if (sigunguSplit[1] === '전체') {
      setFilteredSigungus(
        filteredSigungus.filter((val) => {
          return val.split(';')[0] !== sigunguSplit[0];
        }),
      );
      setCheckIsAllSelectedSido(
        checkIsAllSelectedSido.filter((val) => {
          return val !== sigunguSplit[0];
        }),
      );
    } else {
      setFilteredSigungus(filteredSigungus.filter((val) => val !== sidoSigungu));
    }
  };

  let checkedSigugusList = [];
  const allSelectSigungu = () => {
    const allSigungus = selectedSido + ';전체';
    if (!filteredSigungus.includes(allSigungus)) {
      checkedSigugusList = checkedSigugusList.concat(allSigungus);
      selectedSigungus.map(([, , sigungu_name_kor, sido_name_kor]) => {
        const sidoSigungu = [sido_name_kor, sigungu_name_kor].join(';');
        if (!filteredSigungus.includes(sidoSigungu)) {
          checkedSigugusList = checkedSigugusList.concat(sidoSigungu);
        }
      });
      setFilteredSigungus(filteredSigungus.concat(checkedSigugusList));
    } else {
      setFilteredSigungus(
        filteredSigungus.filter((val) => {
          return val.split(';')[0] !== selectedSido;
        }),
      );
      setCheckIsAllSelectedSido(
        checkIsAllSelectedSido.filter((val) => {
          return val !== selectedSido;
        }),
      );
    }
  };
  return (
    <SSModal2
      isOpen={isModalAddressOpen}
      hasBottomChild={true}
      onClose={() => {
        setIsModalAddressOpen(false);
      }}
      onReset={() => {
        setFilteredSigungus([]);
      }}
      btnSubmitName={'적용하기'}
      btnResetName={'초기화'}
      hasCloseBtn={false}
      hasResetBtn={true}
      titleL="지역"
      titleR="상세 지역"
      childrenL={
        <ul
          className={cx(['modal-body-ul', 'gray-80', 'scrollable-y'])}
          style={{ height: '340px', marginTop: '16px' }}
        >
          {!staticData.sidos.length && <div>Loading!!</div>}
          {staticData.sidos.length &&
            staticData.sidos.map(([num, , name_kor]) => {
              return (
                <li
                  key={num}
                  onClick={() => {
                    setSelectedSido(name_kor);
                  }}
                  style={{ padding: '6px 12px 6px 0px' }}
                >
                  {filteredSigungus.filter((val) => val.split(';')[0] === name_kor).length > 0 && (
                    <span style={{ marginRight: '7px' }}>
                      <IconCheck width={14} height={9.5} fill={'#34C759'} />
                    </span>
                  )}
                  {name_kor}
                </li>
              );
            })}
        </ul>
      }
      childrenR={
        <ul
          className={cx([
            'modal-body-ul',
            'gray-80',
            selectedSigungus.length > 0 ? 'bg-white' : 'bg-gray',
            'scrollable-y',
          ])}
          style={{ width: '507px', height: '340px', marginTop: '16px' }}
        >
          {selectedSigungus.length > 0 && (
            <li className={cx('grid')} onClick={() => allSelectSigungu()}>
              {filteredSigungus.includes(selectedSido + ';전체') && (
                <span style={{ marginRight: '7px' }}>
                  <IconCheck width={14} height={9.5} fill={'#34C759'} />
                </span>
              )}
              전체
            </li>
          )}
          {selectedSigungus.length > 0 &&
            selectedSigungus.map(([, seq, sigungu_name_kor, sido_name_kor]) => {
              const sidoSigungu = [sido_name_kor, sigungu_name_kor].join(';');
              return (
                <li
                  key={seq}
                  className={cx('grid')}
                  onClick={() => {
                    if (!filteredSigungus.includes(sidoSigungu)) {
                      let checkedSigungus = filteredSigungus.concat(sidoSigungu);
                      const checkSidoAllSigungus = checkedSigungus.filter((val) => {
                        return val.split(';')[0] === sido_name_kor;
                      });
                      if (checkSidoAllSigungus.length === selectedSigungus.length) {
                        checkedSigungus = checkedSigungus.concat(sido_name_kor + ';전체');
                        setCheckIsAllSelectedSido(checkIsAllSelectedSido.concat(sido_name_kor));
                      }
                      setFilteredSigungus(checkedSigungus);
                    } else {
                      const filteredSigungu = filteredSigungus.filter((val) => {
                        return val !== sidoSigungu;
                      });
                      const filteredSido = filteredSigungu.filter((val) => {
                        return val !== sido_name_kor + ';전체';
                      });
                      setFilteredSigungus(filteredSido);
                      setCheckIsAllSelectedSido(
                        checkIsAllSelectedSido.filter((val) => {
                          return val !== sido_name_kor;
                        }),
                      );
                    }
                  }}
                >
                  {filteredSigungus.includes(sidoSigungu) && (
                    <span style={{ marginRight: '7px' }}>
                      <IconCheck width={14} height={9.5} fill={'#34C759'} />
                    </span>
                  )}
                  {sigungu_name_kor}
                </li>
              );
            })}
          {selectedSigungus.length === 0 && (
            <div style={{ padding: '120px 50px', textAlign: 'center' }}>
              <IconArrowCircleLeft width={24} height={24} />
              <p>지역 선택 후</p>
              <p>상세 지역을 확인할 수 있습니다.</p>
            </div>
          )}
        </ul>
      }
      childrenB={
        <div className={cx(['modal-bottom'])}>
          {filteredSigungus.length > 0 &&
            filteredSigungus.map((sigungu, index) => {
              const sigunguSplit = sigungu.split(';');
              if (sigunguSplit[1] === '전체') {
                if (!checkIsAllSelectedSido.includes(sigunguSplit[0])) {
                  setCheckIsAllSelectedSido(checkIsAllSelectedSido.concat(sigunguSplit[0]));
                }
                return (
                  <ModalSigunguBox
                    key={index}
                    sigungu={sigungu}
                    sigunguSplit={sigunguSplit}
                    deleteSido={deleteSido}
                  />
                );
              } else if (!checkIsAllSelectedSido.includes(sigunguSplit[0])) {
                return (
                  <ModalSigunguBox
                    key={index}
                    sigungu={sigungu}
                    sigunguSplit={sigunguSplit}
                    deleteSido={deleteSido}
                  />
                );
              }
            })}
        </div>
      }
      customStyle={{ content: { width: '736px' } }}
    />
  );
};

const ModalSigunguBox = ({ sigungu, sigunguSplit, deleteSido }) => {
  return (
    <div className={cx(['selected-chip-box'])}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className={cx(['selected-chip'])}>
          {sigunguSplit[0]} {sigunguSplit[1]}
        </div>
        <IconX
          style={{
            width: '12px',
            height: '12px',
            marginLeft: '4px',
            cursor: 'pointer',
          }}
          onClick={() => deleteSido(sigungu)}
        />
      </div>
    </div>
  );
};

const CheckboxWrapper = ({ array, name, handleFilter, checkedValues, size }) => {
  const [checkedState, setCheckedState] = useState(
    array.map(([value]) => {
      return checkedValues.includes(value);
    }),
  );
  const [isReset, setIsReset] = useState(checkedValues.length === 0 ? true : false);

  useEffect(() => {
    if (checkedValues.length === 0) {
      setIsReset(true);
      setCheckedState(
        array.map(() => {
          return false;
        }),
      );
    } else {
      setIsReset(false);
    }
  }, [checkedValues]);

  const handleOnChange = (idxChanged) => {
    const updated = checkedState.map((checked, idx) => {
      return idx === idxChanged ? !checked : checked;
    });
    setCheckedState(updated);
    handleFilter(
      array
        .filter((elem, idx) => {
          return updated[idx];
        })
        .map(([value]) => {
          return value;
        }),
    );
  };

  return (
    <ul className={cx('modal-body-ul-checkbox')}>
      {array.map(([value, text], idx) => {
        return (
          <li key={idx}>
            <Checkbox
              size={size}
              name={name}
              value={value}
              checked={checkedState[idx]}
              updateContainer={() => {
                handleOnChange(idx);
              }}
              isReset={isReset}
            >
              {text}
            </Checkbox>
          </li>
        );
      })}
    </ul>
  );
};

// Mobile
const MJobDisplayingListPresenter = ({
  setSavedFilters,
  isMFilterSectionOpen,
  isModalStackOpen,
  isModalRoleOpen,
  isModalLevelOpen,
  isModalAddressOpen,
  setIsMFilterSectionOpen,
  setIsModalStackOpen,
  setIsModalRoleOpen,
  setIsModalLevelOpen,
  setIsModalAddressOpen,
  isFilterSaveModalOpen,
  isFilterApplyModalOpen,
  setIsFilterSaveModalOpen,
  setIsFilterApplyModalOpen,
  filters,
  mutate,
  staticData,
  jobs,
  total_jobs_cnt,
  page,
  handlePageChange,
  keyword,
  setKeyword,
  isLoading,
}) => {
  const [tempKeyword_, setTempKeyword_] = useState('');
  const [sort_, setSort_] = useRecoilState(JobSort);
  const router = useRouter();

  useEffect(() => {
    if (page < 1) {
      handlePageChange(null, 1);
    }
    if (total_jobs_cnt >= 0) {
      const maxPage =
        total_jobs_cnt === 0 ? 1 : Math.floor((total_jobs_cnt - 1) / PAGINATED_BY) + 1;
      if (page > maxPage) {
        handlePageChange(null, maxPage);
      }
    }
  }, [page, total_jobs_cnt]);

  useEffect(() => {
    setTempKeyword_(keyword);
  }, [keyword]);

  useDebouncedEffect(
    () => {
      setKeyword(tempKeyword_);
    },
    1000,
    [tempKeyword_],
  );

  return (
    <>
      <section className={mcx('layout')}>
        <section className={mcx('list')}>
          <section className={mcx('filter')}>
            <div className={mcx('filter-search')}>
              <div className={mcx('search-box')}>
                <InputSearchGroup size={'lg'}>
                  <Input
                    size={'sm'}
                    placeholder={'기업, 공고를 검색해보세요.'}
                    value={tempKeyword_}
                    onChange={(e) => {
                      setTempKeyword_(e.target.value);
                    }}
                  />
                </InputSearchGroup>
              </div>
              <span
                className={mcx('btn-filter')}
                onClick={() => {
                  setIsMFilterSectionOpen(true);
                }}
              >
                <IconFilter width={16} height={14} fill={'#006DFF'} />
                필터
              </span>
            </div>
            <MFilterSection
              isMFilterSectionOpen={isMFilterSectionOpen}
              setIsMFilterSectionOpen={setIsMFilterSectionOpen}
              setIsModalStackOpen={setIsModalStackOpen}
              setIsModalRoleOpen={setIsModalRoleOpen}
              setIsModalLevelOpen={setIsModalLevelOpen}
              setIsModalAddressOpen={setIsModalAddressOpen}
              isFilterApplyModalOpen={isFilterApplyModalOpen}
              setIsFilterApplyModalOpen={setIsFilterApplyModalOpen}
              filters={filters}
              setSavedFilters={setSavedFilters}
              isFilterSaveModalOpen={isFilterSaveModalOpen}
              setIsFilterSaveModalOpen={setIsFilterSaveModalOpen}
              updateContainer={mutate}
            />
            <MStackModal
              isModalStackOpen={isModalStackOpen}
              setIsModalStackOpen={setIsModalStackOpen}
              stacks={staticData.stacks}
            />
            <MRoleModal
              isModalRoleOpen={isModalRoleOpen}
              setIsModalRoleOpen={setIsModalRoleOpen}
              roles={staticData.roles}
            />
            <MLevelModal
              isModalLevelOpen={isModalLevelOpen}
              setIsModalLevelOpen={setIsModalLevelOpen}
              levels={staticData.levels}
            />
            <MSidoSigunguModal
              isModalAddressOpen={isModalAddressOpen}
              setIsModalAddressOpen={setIsModalAddressOpen}
              staticData={staticData}
            />
          </section>
          <section className={mcx('list-header')} id="list_header">
            {!isLoading && total_jobs_cnt > 0 && (
              <div className={mcx(['metadata'])}>
                <p>{total_jobs_cnt}개의 포지션</p>
                <JobSortWrapper sort={sort_} setSort={setSort_} />
              </div>
            )}
          </section>
          <section className={mcx('list-body')}>
            <FadeLoader
              color={'gray'}
              loading={isLoading}
              cssOverride={{
                margin: '200px auto',
              }}
            />
            <CardJobDividedList
              jobs={jobs}
              showEmpty={total_jobs_cnt === 0}
              handleClick={(job) => {
                const url = JobLinks(job.id).find((link) => link.name === 'job_detail').url;
                router.push(url, url);
              }}
              isMobile={true}
            />
            {total_jobs_cnt > 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <SSPagination
                  countItems={total_jobs_cnt}
                  paginatiedBy={PAGINATED_BY}
                  page={page}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </section>
        </section>
      </section>
    </>
  );
};

const MFilterSection = ({
  isMFilterSectionOpen,
  setIsMFilterSectionOpen,
  setIsModalStackOpen,
  setIsModalRoleOpen,
  setIsModalLevelOpen,
  setIsModalAddressOpen,

  isFilterApplyModalOpen,
  setIsFilterApplyModalOpen,
  filters,
  setSavedFilters,

  isFilterSaveModalOpen,
  setIsFilterSaveModalOpen,
  updateContainer,
}) => {
  const setFilteredStacks = useSetRecoilState(FilteredStacks);
  const setFilteredRoles = useSetRecoilState(FilteredRoles);
  const setFilteredLevels = useSetRecoilState(FilteredLevels);
  const setFilteredSigungus = useSetRecoilState(FilteredSigungus);

  return (
    <>
      <MSSModal
        isOpen={isMFilterSectionOpen}
        onClose={() => {
          setIsMFilterSectionOpen(false);
        }}
        onReset={() => {
          setFilteredStacks({});
          setFilteredRoles([]);
          setFilteredLevels([]);
          setFilteredSigungus([]);
        }}
        btnSubmitName={'포지션 보기'}
        title={'필터'}
        btnResetName={'초기화'}
        hasCloseBtn={false}
        hasResetBtn={true}
        featureBtn={{
          size: 'xxl',
          mode: 'stroke',
          content: '저장하기',
          featureFn: () => {
            setIsFilterSaveModalOpen(true);
          },
        }}
      >
        {filters && filters.length > 0 && (
          <>
            <div className={mcx('m-filter-box')}>
              <a
                className={mcx('link-area')}
                onClick={() => {
                  setIsFilterApplyModalOpen(true);
                }}
              >
                <div className={mcx('content-box')}>
                  <IconSave width={18} height={20} fill={'black'} />
                  <div className={mcx('content-with-icon')}>저장한 필터</div>
                </div>
                <IconArrowRight width={9} height={17} fill={'#000000'} />
              </a>
            </div>
            <MFilterApplyModal
              isFilterApplyModalOpen={isFilterApplyModalOpen}
              setIsFilterApplyModalOpen={setIsFilterApplyModalOpen}
              filters={filters}
              setSavedFilters={setSavedFilters}
            />
          </>
        )}
        <ul
          className={mcx('m-link-box-container')}
          style={{ borderTop: filters && filters.length > 0 ? 'none' : '1px solid #EEEEEE' }}
        >
          <li className={mcx('m-link-box')}>
            <a
              className={mcx('link-area')}
              onClick={() => {
                setIsModalStackOpen(true);
              }}
            >
              <span>기술 스택 필터</span>
              <IconArrowRight width={9} height={17} fill="#000000" />
            </a>
            <ChipFilterContainer
              type={'stack'}
              isMobile={true}
              cx={cx}
              mcx={mcx}
              style={{ paddingBottom: '16px' }}
            />
          </li>
          <li className={mcx('m-link-box')}>
            <a
              className={mcx('link-area')}
              onClick={() => {
                setIsModalRoleOpen(true);
              }}
            >
              <span>역할 필터</span>
              <IconArrowRight width={9} height={17} fill="#000000" />
            </a>
            <ChipFilterContainer
              type={'role'}
              isMobile={true}
              cx={cx}
              mcx={mcx}
              style={{ paddingBottom: '16px' }}
            />
          </li>
          <li className={mcx('m-link-box')}>
            <a
              className={mcx('link-area')}
              onClick={() => {
                setIsModalLevelOpen(true);
              }}
            >
              <span>레벨 필터</span>
              <IconArrowRight width={9} height={17} fill="#000000" />
            </a>
            <ChipFilterContainer
              type={'level'}
              isMobile={true}
              cx={cx}
              mcx={mcx}
              style={{ paddingBottom: '16px' }}
            />
          </li>
          <li className={mcx('m-link-box')}>
            <a
              className={mcx('link-area')}
              onClick={() => {
                setIsModalAddressOpen(true);
              }}
            >
              <span>지역 필터</span>
              <IconArrowRight width={9} height={17} fill="#000000" />
            </a>
            <ChipFilterContainer
              type={'sigungu'}
              isMobile={true}
              cx={cx}
              mcx={mcx}
              style={{ paddingBottom: '16px' }}
            />
          </li>
        </ul>
        <MFilterSaveModal
          isFilterSaveModalOpen={isFilterSaveModalOpen}
          setIsFilterSaveModalOpen={setIsFilterSaveModalOpen}
          updateContainer={() => {
            updateContainer();
          }}
        />
      </MSSModal>
    </>
  );
};

const MRoleModal = ({ isModalRoleOpen, setIsModalRoleOpen, roles }) => {
  const [filteredRoles, setFilteredRoles] = useRecoilState(FilteredRoles);
  return (
    <>
      <MSSModal
        isOpen={isModalRoleOpen}
        onClose={() => {
          setIsModalRoleOpen(false);
        }}
        onReset={() => {
          setFilteredRoles([]);
        }}
        title={'역할 필터'}
        btnResetName={'초기화'}
        hasCloseBtn={false}
        hasResetBtn={true}
      >
        {roles && (
          <CheckboxWrapper
            array={roles}
            name="roles"
            size="lg"
            handleFilter={setFilteredRoles}
            checkedValues={filteredRoles}
          />
        )}
      </MSSModal>
    </>
  );
};

const MLevelModal = ({ isModalLevelOpen, setIsModalLevelOpen, levels }) => {
  const [filteredLevels, setFilteredLevels] = useRecoilState(FilteredLevels);
  return (
    <>
      <MSSModal
        isOpen={isModalLevelOpen}
        onClose={() => {
          setIsModalLevelOpen(false);
        }}
        onReset={() => {
          setFilteredLevels([]);
        }}
        title={'레벨 필터'}
        btnResetName={'초기화'}
        hasCloseBtn={false}
        hasResetBtn={true}
      >
        {levels && (
          <CheckboxWrapper
            array={levels}
            name="levels"
            size="lg"
            handleFilter={setFilteredLevels}
            checkedValues={filteredLevels}
          />
        )}
      </MSSModal>
    </>
  );
};

const MSidoSigunguModal = ({ isModalAddressOpen, setIsModalAddressOpen, staticData }) => {
  const [filteredSigungus, setFilteredSigungus] = useRecoilState(FilteredSigungus);
  const [selectedSido, setSelectedSido] = useState();
  const [checkIsAllSelectedSido, setCheckIsAllSelectedSido] = useState([]);

  useEffect(() => {
    document.documentElement.style.setProperty('--innerVh', window.innerHeight * 0.01);
  }, []);

  const selectedSigungus =
    staticData.sigungus.length > 0 &&
    staticData.sigungus.filter(([, , , sido_name_kor]) => {
      return sido_name_kor === selectedSido;
    });

  let checkedSigugusList = [];
  const allSelectSigungu = () => {
    const allSigungus = selectedSido + ';전체';
    if (!filteredSigungus.includes(allSigungus)) {
      checkedSigugusList = checkedSigugusList.concat(allSigungus);
      selectedSigungus.map(([, , sigungu_name_kor, sido_name_kor]) => {
        const sidoSigungu = [sido_name_kor, sigungu_name_kor].join(';');
        if (!filteredSigungus.includes(sidoSigungu)) {
          checkedSigugusList = checkedSigugusList.concat(sidoSigungu);
        }
      });
      setFilteredSigungus(filteredSigungus.concat(checkedSigugusList));
    } else {
      setFilteredSigungus(
        filteredSigungus.filter((val) => {
          return val.split(';')[0] !== selectedSido;
        }),
      );
      setCheckIsAllSelectedSido(
        checkIsAllSelectedSido.filter((val) => {
          return val !== selectedSido;
        }),
      );
    }
  };

  return (
    <>
      <MSSModal
        isOpen={isModalAddressOpen}
        onClose={() => {
          setIsModalAddressOpen(false);
        }}
        onReset={() => {
          setFilteredSigungus([]);
        }}
        filteredItems={filteredSigungus}
        setFilteredItems={setFilteredSigungus}
        checkIsAllSelectedSido={checkIsAllSelectedSido}
        setCheckIsAllSelectedSido={setCheckIsAllSelectedSido}
        title={'지역 필터'}
        btnResetName={'초기화'}
        BottomBoxChildren={
          <MSelectedSigunguChips
            filteredItems={filteredSigungus}
            setFilteredItems={setFilteredSigungus}
            checkIsAllSelectedSido={checkIsAllSelectedSido}
            setCheckIsAllSelectedSido={setCheckIsAllSelectedSido}
          />
        }
        hasCloseBtn={false}
        hasResetBtn={true}
      >
        <div className={mcx('sigungu-wrapper')}>
          <ul className={mcx(['modal-body-ul', 'gray-80'])} style={{ minWidth: '120px' }}>
            {!staticData.sidos.length && <div>Loading!!</div>}
            {staticData.sidos.length &&
              staticData.sidos.map(([num, , name_kor]) => {
                return (
                  <li
                    key={num}
                    className={mcx('clickable')}
                    onClick={() => {
                      setSelectedSido(name_kor);
                    }}
                  >
                    {filteredSigungus.filter((val) => val.split(';')[0] === name_kor).length >
                      0 && (
                      <span style={{ marginRight: '7px' }}>
                        <IconCheck width={14} height={9.5} fill={'#34C759'} />
                      </span>
                    )}
                    {name_kor}
                  </li>
                );
              })}
          </ul>
          <ul
            className={mcx([
              'modal-body-ul',
              'gray-80',
              selectedSigungus.length > 0 ? 'bg-white' : 'bg-gray',
            ])}
            style={{ width: '100%' }}
          >
            {selectedSigungus.length > 0 && (
              <li onClick={allSelectSigungu} className={mcx('clickable')}>
                {filteredSigungus.includes(selectedSido + ';전체') && (
                  <span style={{ marginRight: '7px' }}>
                    <IconCheck width={14} height={9.5} fill={'#34C759'} />
                  </span>
                )}
                전체
              </li>
            )}
            {selectedSigungus.length > 0 &&
              selectedSigungus.map(([, seq, sigungu_name_kor, sido_name_kor]) => {
                const sidoSigungu = [sido_name_kor, sigungu_name_kor].join(';');
                return (
                  <li
                    key={seq}
                    className={mcx('clickable')}
                    onClick={() => {
                      if (!filteredSigungus.includes(sidoSigungu)) {
                        let checkedSigungus = filteredSigungus.concat(sidoSigungu);
                        const checkSidoAllSigungus = checkedSigungus.filter((val) => {
                          return val.split(';')[0] === sido_name_kor;
                        });
                        if (checkSidoAllSigungus.length === selectedSigungus.length) {
                          checkedSigungus = checkedSigungus.concat(sido_name_kor + ';전체');
                          setCheckIsAllSelectedSido(checkIsAllSelectedSido.concat(sido_name_kor));
                        }
                        setFilteredSigungus(checkedSigungus);
                      } else {
                        const filteredSigungu = filteredSigungus.filter((val) => {
                          return val !== sidoSigungu;
                        });
                        const filteredSido = filteredSigungu.filter((val) => {
                          return val !== sido_name_kor + ';전체';
                        });
                        setFilteredSigungus(filteredSido);
                        setCheckIsAllSelectedSido(
                          checkIsAllSelectedSido.filter((val) => {
                            return val !== sido_name_kor;
                          }),
                        );
                      }
                    }}
                  >
                    {filteredSigungus.includes(sidoSigungu) && (
                      <span style={{ marginRight: '7px' }}>
                        <IconCheck width={14} height={9.5} fill={'#34C759'} />
                      </span>
                    )}
                    {sigungu_name_kor}
                  </li>
                );
              })}
            {selectedSigungus.length === 0 && (
              <div className={mcx(['info-text', 'gray-80'])}>
                <IconArrowCircleLeft width={24} height={24} />
                <div>지역 선택 후</div>
                <div>상세 지역을 확인할 수 있습니다.</div>
              </div>
            )}
          </ul>
        </div>
      </MSSModal>
    </>
  );
};

export async function getServerSideProps(context) {
  const { req } = context;
  const config = { headers: { cookie: req.headers.cookie || '' } };
  const userInfoUrl = ApiUrls().find((url) => url.name === 'ss_user_info').url;
  const userInfoData = await internalApi.get(userInfoUrl, config).then((result) => result.data);
  return {
    props: { userInfoData: userInfoData },
  };
}

export default JobDisplayingListWrapper;
